import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from 'lib/supabase';
import Icon from 'components/AppIcon';
import logoImage from '/assets/images/logo-volaptic-1775131861970.png';

// Fallback icon definitions so the navbar can render icons without receiving them as props
const AVATAR_ICONS_FALLBACK = [
  { id: 'person', label: 'Persona', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
      <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
    </svg>
  )},
  { id: 'house', label: 'Casa', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z"/><path d="M9 21V12h6v9"/>
    </svg>
  )},
  { id: 'star', label: 'Stella', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  )},
  { id: 'heart', label: 'Cuore', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
    </svg>
  )},
  { id: 'lightning', label: 'Fulmine', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  )},
  { id: 'leaf', label: 'Foglia', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
      <path d="M17 8C8 10 5.9 16.17 3.82 19.34a1 1 0 001.38 1.4C7.2 19.1 9.5 17 12 17c4 0 8-3 8-8 0-3-1-5-3-7z"/>
      <path d="M3 21c0-4 2-7 5-9"/>
    </svg>
  )},
  { id: 'smile', label: 'Sorriso', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
      <circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>
    </svg>
  )},
  { id: 'rocket', label: 'Razzo', svg: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09z"/>
      <path d="M12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z"/>
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
    </svg>
  )},
];

const ClientDashboardNavbar = ({ selectedAvatar, avatarIcons, overrideUserName }) => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [userName, setUserName] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dbAvatarIcon, setDbAvatarIcon] = useState(null);
  const [dbAvatarUrl, setDbAvatarUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchUserData = async () => {
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (!user || cancelled) return;

        const { data, error } = await supabase?.from('users')?.select('full_name, avatar_icon, avatar_url')?.eq('id', user?.id)?.single();

        if (cancelled) return;

        if (!error && data?.full_name && data?.full_name?.trim() !== '') {
          setUserName(data?.full_name);
        } else {
          const namePart = user?.email?.split('@')?.[0] ?? '';
          setUserName(namePart?.charAt(0)?.toUpperCase() + namePart?.slice(1));
        }

        if (!error && data?.avatar_icon) {
          setDbAvatarIcon(data?.avatar_icon);
        } else {
          setDbAvatarIcon(null);
        }

        if (!error && data?.avatar_url) {
          setDbAvatarUrl(data?.avatar_url);
        } else {
          setDbAvatarUrl(null);
        }
      } catch (err) {
        // silent fail
      }
    };

    setUserName('');
    setDbAvatarIcon(null);
    setDbAvatarUrl(null);
    fetchUserData();

    const onFocus = () => { if (!cancelled) fetchUserData(); };
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  useEffect(() => {
    const handleAvatarUpdate = (e) => {
      const { icon, url } = e?.detail || {};
      setDbAvatarUrl(url ?? null);
      setDbAvatarIcon(icon ?? null);
    };
    window.addEventListener('volaptic-avatar-update', handleAvatarUpdate);
    return () => {
      window.removeEventListener('volaptic-avatar-update', handleAvatarUpdate);
    };
  }, []);

  const handleLogout = async () => {
    setMobileMenuOpen(false);
    const { error } = await signOut();
    if (!error) {
      navigate('/login-form');
    }
  };

  const displayName = overrideUserName || userName;
  const initials = displayName ? displayName?.slice(0, 2)?.toUpperCase() : '??';

  // Determine what to show in the avatar circle
  const renderAvatarContent = () => {
    // If photo URL is set, render the photo
    if (dbAvatarUrl) {
      return (
        <img
          src={dbAvatarUrl}
          alt="Avatar"
          style={{
            width: '36px', height: '36px', borderRadius: '50%',
            objectFit: 'cover', flexShrink: 0,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      );
    }
    // Prefer DB avatar_icon, fall back to prop, then initials
    const activeAvatarId = dbAvatarIcon || selectedAvatar;
    const iconList = (avatarIcons && avatarIcons?.length > 0)
      ? avatarIcons
      : AVATAR_ICONS_FALLBACK;

    if (activeAvatarId) {
      const icon = iconList?.find(a => a?.id === activeAvatarId);
      if (icon) {
        return (
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {icon?.svg('#fff')}
          </span>
        );
      }
    }
    return (
      <span style={{ color: '#fff', fontWeight: 700, fontSize: '13px', letterSpacing: '0.5px' }}>
        {initials}
      </span>
    );
  };

  return (
    <header
      className="header-nav"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px', width: '100%' }}
    >
      {/* Logo */}
      <Link to="/" className="header-logo flex items-center">
        <img
          src={logoImage}
          alt="Volaptic"
          style={{ height: '56px', width: 'auto' }}
        />
      </Link>

      {/* Desktop: user identity + settings + logout */}
      <div className="hidden md:flex items-center gap-3">
        {/* Dashboard link */}
        <Link
          to="/cliente/dashboard"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            padding: '6px 12px',
            borderRadius: '8px',
            color: '#013776',
            fontSize: '13px',
            fontWeight: 600,
            textDecoration: 'none',
            transition: 'background 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <Icon name="LayoutDashboard" size={15} color="#013776" />
          Dashboard
        </Link>

        {/* Avatar + Name/Role */}
        <div className="flex items-center gap-2.5">
          {/* Gradient Avatar */}
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #32b3e5 0%, #013776 100%)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              overflow: 'hidden',
            }}
          >
            {renderAvatarContent()}
          </div>
          {/* Name + Role */}
          <div className="flex flex-col leading-tight">
            {displayName && (
              <span style={{ color: '#013776', fontWeight: 600, fontSize: '14px', lineHeight: '1.2' }}>
                {displayName}
              </span>
            )}
            <span style={{ color: '#32b3e5', fontSize: '11px', fontWeight: 500, lineHeight: '1.3', marginTop: '1px' }}>
              Cliente
            </span>
          </div>
        </div>

        {/* Settings icon-only button */}
        <div className="relative group">
          <button
            onClick={() => navigate('/cliente/settings')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '7px',
              borderRadius: '8px',
              color: '#32b3e5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
            aria-label="Impostazioni"
          >
            <Icon name="Settings" size={18} color="#32b3e5" />
          </button>
          {/* Tooltip */}
          <span
            style={{
              position: 'absolute',
              top: '110%',
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#013776',
              color: '#fff',
              fontSize: '11px',
              fontWeight: 500,
              padding: '3px 8px',
              borderRadius: '5px',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              opacity: 0,
              transition: 'opacity 0.15s',
              zIndex: 10,
            }}
            className="group-hover:opacity-100"
          >
            Impostazioni
          </span>
        </div>

        {/* Thin vertical separator */}
        <div
          style={{
            width: '1px',
            height: '28px',
            background: '#e2e8f0',
            flexShrink: 0,
          }}
        />

        {/* Esci pill button */}
        <button
          onClick={handleLogout}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 16px',
            borderRadius: '999px',
            border: '1.5px solid #32b3e5',
            background: 'transparent',
            color: '#013776',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background 0.2s, color 0.2s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = '#32b3e5';
            e.currentTarget.style.color = '#fff';
            const icon = e?.currentTarget?.querySelector('svg');
            if (icon) icon.style.stroke = '#fff';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = '#013776';
            const icon = e?.currentTarget?.querySelector('svg');
            if (icon) icon.style.stroke = '#013776';
          }}
        >
          <Icon name="LogOut" size={14} color="#013776" />
          Esci
        </button>
      </div>

      {/* Mobile: hamburger icon */}
      <div className="flex md:hidden items-center">
        <button
          onClick={() => setMobileMenuOpen((prev) => !prev)}
          className="p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          aria-label="Menu"
        >
          <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={24} />
        </button>
      </div>

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <div
          className="absolute top-16 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-lg px-6 py-4 flex flex-col gap-3 md:hidden"
        >
          {/* User identity */}
          <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #32b3e5 0%, #013776 100%)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                overflow: 'hidden',
              }}
            >
              {renderAvatarContent()}
            </div>
            <div>
              {displayName && (
                <p style={{ color: '#013776', fontWeight: 600, fontSize: '14px' }}>{displayName}</p>
              )}
              <p style={{ color: '#32b3e5', fontSize: '11px', fontWeight: 500 }}>Cliente</p>
            </div>
          </div>
          {/* Dashboard link */}
          <Link
            to="/cliente/dashboard"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-2 text-sm font-medium py-2 transition-colors"
            style={{ color: '#013776', textDecoration: 'none' }}
          >
            <Icon name="LayoutDashboard" size={16} color="#013776" />
            Dashboard
          </Link>
          {/* Settings link */}
          <button
            onClick={() => { setMobileMenuOpen(false); navigate('/cliente/settings'); }}
            className="flex items-center gap-2 text-sm font-medium py-2 transition-colors"
            style={{ color: '#32b3e5' }}
          >
            <Icon name="Settings" size={16} color="#32b3e5" />
            Impostazioni
          </button>
          {/* Logout */}
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm font-medium py-2 text-red-600 hover:text-red-700 transition-colors"
          >
            <Icon name="LogOut" size={16} />
            Esci
          </button>
        </div>
      )}
    </header>
  );
};

export default ClientDashboardNavbar;
